import axios from "axios";

// Production is served behind the same HTTPS origin as the API. Keeping this
// relative prevents a remote browser from trying to call its own 127.0.0.1.
const BACKEND_URL = process.env.NODE_ENV === "production"
  ? ""
  : (process.env.REACT_APP_BACKEND_URL || "");
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "clonecast_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const saveToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const api = axios.create({ baseURL: API });

// Route transitions need to distinguish "the page has no data" from "the page
// has not received its data yet".  Axios is the one common boundary shared by
// every dashboard section, so expose a tiny read-only activity signal for the
// layout's navigation cover rather than teaching every page the same protocol.
let pendingRequests = 0;
const activityListeners = new Set();
const emitActivity = () => activityListeners.forEach((listener) => listener(pendingRequests));
const requestStarted = () => { pendingRequests += 1; emitActivity(); };
const requestFinished = () => { pendingRequests = Math.max(0, pendingRequests - 1); emitActivity(); };
export const getPendingRequestCount = () => pendingRequests;
export const subscribeToApiActivity = (listener) => {
  activityListeners.add(listener);
  return () => activityListeners.delete(listener);
};

// Normalize any API error into a plain string (FastAPI 422 returns a structured
// `detail` array which crashes React if passed straight to a toast/JSX).
export const errMsg = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join(", ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return err?.message || fallback;
};

api.interceptors.request.use((config) => {
  requestStarted();
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
}, (err) => {
  requestFinished();
  return Promise.reject(err);
});

api.interceptors.response.use(
  (res) => {
    requestFinished();
    return res;
  },
  (err) => {
    requestFinished();
    if (err.response?.status === 401) {
      clearToken();
      window.dispatchEvent(new Event("clonecast:unauthorized"));
    }
    return Promise.reject(err);
  }
);

export default api;
